/* Link a device user (PIN) to an employee — searchable employee picker + "all devices". */
import { useMemo, useState } from "react";
import type { UnmatchedUser } from "../../../data/types";
import { useAllEmployees, useMapUser } from "../../../data/hooks";
import { Button, Modal, Person, SearchBox, Skeleton, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";

export function LinkEmployeeModal({ user, deviceName, onClose }: { user: UnmatchedUser; deviceName: string; onClose: () => void }) {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const emps = useAllEmployees();
  const map = useMapUser();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const [err, setErr] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const act = (emps.data ?? []).filter((e) => e.status !== "Terminated");
    // A device PIN that equals an employee code/id is the most likely match — float it to the top.
    const guess = (e: { id: number; code: string }) => (String(e.id) === user.deviceUserId || e.code.toLowerCase() === user.deviceUserId.toLowerCase() ? 0 : 1);
    return act.filter((e) => !s || e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s) || String(e.id).includes(s))
      .sort((a, b) => guess(a) - guess(b) || a.id - b.id);
  }, [emps.data, q, user.deviceUserId]);

  const save = () => {
    if (sel == null) { setErr(L("Choose an employee", "اختر موظفًا")); return; }
    map.mutate({ deviceId: all ? "*" : user.deviceId, deviceUserId: user.deviceUserId, employeeId: sel }, {
      onSuccess: () => { toast(L(`${user.punches} punches linked — attendance updated`, `تم ربط ${user.punches} بصمة — تم تحديث الحضور`)); onClose(); },
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };

  return (
    <Modal title={L("Link to employee", "ربط بموظف")} onClose={onClose} testId="link-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={save} loading={map.isPending} data-testid="link-save">{L("Link", "ربط")}</Button></>}>
      <div className="stack">
        <p className="sm" style={{ margin: 0 }}>
          {L("Device user", "مستخدم الجهاز")} <b className="num" dir="ltr">{user.deviceUserId}</b>{user.name && <> · {user.name}</>} · {deviceName}
          <br /><span className="muted xs">{L(`${user.punches} punches will be linked and attendance recalculated.`, `سيتم ربط ${user.punches} بصمة وإعادة احتساب الحضور.`)}</span>
        </p>
        <SearchBox value={q} onChange={setQ} placeholder={L("Search name, code or ID…", "ابحث بالاسم أو الرمز أو الرقم…")} testId="link-search" />
        {emps.data ? (
          <div className="emp-pick" role="listbox" aria-label={t("Employee")} data-testid="link-list">
            {list.slice(0, 50).map((e) => (
              <button key={e.id} type="button" role="option" aria-selected={sel === e.id} className={sel === e.id ? "on" : ""} onClick={() => { setSel(e.id); setErr(""); }} data-testid={"pick-" + e.id}>
                <Person e={e} />
              </button>
            ))}
            {!list.length && <div className="sm muted" style={{ padding: 10 }}>{L("No employees match", "لا يوجد موظفون مطابقون")}</div>}
          </div>
        ) : <Skeleton />}
        {err && <span className="err" role="alert">{err}</span>}
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="link-all" />
          <span className="sm">{L("Link on all devices (same user ID everywhere)", "الربط على كل الأجهزة (نفس رقم المستخدم في كل مكان)")}</span>
        </label>
      </div>
    </Modal>
  );
}
